import React from 'react';
import {
  AIRBNB_ADVANCE_NOTICE_OPTIONS,
  AIRBNB_PREPARATION_TIME_OPTIONS,
  AIRBNB_SAME_DAY_CUTOFF_OPTIONS,
} from '../../utils/hostSyncSettings';

const font = "'Inria Serif', serif";

const selectCardStyle = {
  width: '100%',
  borderRadius: 28,
  border: '1px solid rgba(112, 63, 246, 0.12)',
  background: 'var(--surface)',
  boxShadow: 'var(--premium-shadow-nested)',
  padding: '18px 18px 16px',
  display: 'grid',
  gap: 12,
};

const selectStyle = {
  width: '100%',
  borderRadius: 18,
  border: '1px solid rgba(112, 63, 246, 0.14)',
  background: 'var(--surface)',
  padding: '14px 16px',
  fontFamily: font,
  fontSize: 18,
  color: 'var(--text)',
  outline: 'none',
  boxShadow: 'none',
};

const SelectCard = ({ title, description, testId, value, onChange, options }) => (
  <div data-testid={`${testId}-card`} style={selectCardStyle}>
    <div style={{ display: 'grid', gap: 6 }}>
      <p style={{ fontFamily: font, fontSize: 20, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.1 }}>{title}</p>
      {description ? <p style={{ fontFamily: font, fontSize: 15, color: 'var(--text-2)', margin: 0, lineHeight: 1.3 }}>{description}</p> : null}
    </div>
    <select data-testid={testId} value={value} onChange={onChange} style={selectStyle}>
      {options.map((option) => (
        <option key={option.value} value={option.value}>{option.label}</option>
      ))}
    </select>
  </div>
);

export const HostAirbnbSyncSettingsCard = ({
  testIdPrefix,
  value,
  onChange,
  footer,
  introTitle = 'Match these settings with Airbnb',
  introCopy = 'Use the same Airbnb availability settings here to reduce false reservation detections and extra sync alerts.',
  introCopyColor = '#5F5978',
  showIntroCard = true,
}) => (
  <div style={{ width: '100%', display: 'grid', gap: 12 }}>
    {showIntroCard ? (
      <div className="premium-card" data-testid={`${testIdPrefix}-intro-card`} style={{ width: '100%', padding: '18px 18px 16px', display: 'grid', gap: 8 }}>
        <p style={{ fontFamily: font, fontSize: 21, fontWeight: 700, color: 'var(--text)', margin: 0, lineHeight: 1.08 }}>{introTitle}</p>
        <p data-testid={`${testIdPrefix}-intro-copy`} style={{ fontFamily: font, fontSize: 15, color: introCopyColor, margin: 0, lineHeight: 1.32 }}>{introCopy}</p>
      </div>
    ) : null}

    <SelectCard
      title="Advance notice"
      description="Choose the same notice window you use on Airbnb before a guest can book your stay."
      testId={`${testIdPrefix}-advance-notice-select`}
      value={value.airbnbAdvanceNotice}
      onChange={(event) => onChange({ ...value, airbnbAdvanceNotice: event.target.value, airbnbSameDayCutoffHour: event.target.value === 'same_day' ? value.airbnbSameDayCutoffHour : null })}
      options={AIRBNB_ADVANCE_NOTICE_OPTIONS}
    />

    {value.airbnbAdvanceNotice === 'same_day' ? (
      <SelectCard
        title="Same day advance notice"
        description="Guests can book on the same day as check-in until this time."
        testId={`${testIdPrefix}-same-day-cutoff-select`}
        value={value.airbnbSameDayCutoffHour ?? 17}
        onChange={(event) => onChange({ ...value, airbnbSameDayCutoffHour: Number(event.target.value) })}
        options={AIRBNB_SAME_DAY_CUTOFF_OPTIONS}
      />
    ) : null}

    <SelectCard
      title="Preparation time"
      description="How many nights before and after each reservation do you need Airbnb to keep blocked off?"
      testId={`${testIdPrefix}-preparation-time-select`}
      value={value.airbnbPreparationTimeNights}
      onChange={(event) => onChange({ ...value, airbnbPreparationTimeNights: Number(event.target.value) })}
      options={AIRBNB_PREPARATION_TIME_OPTIONS}
    />

    {footer ? <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>{footer}</div> : null}
  </div>
);
